import { useState, useCallback, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import ReportTableHeader from "../../../../containers/DocumentsReport/ReportTableHeader/ReportTableHeader";
import AddEditPassword from "./AddEditPassword/AddEditPassword";
import Table from "./Table/Table";

import { loadLegalEntitySIs } from "../../../../services/legal-entity-sis/legal-entity-sis-api.service";
import { Page } from "../../../../models/api/page";

import useSearchOptions from "../../../../custom-hooks/useSearchOptions/useSearchOptions";

import { FT_LESI } from "../../../../utils/constants/filter-tables-constants";

import { State } from "./interfaces";
import { Container } from "./styles";

import { ComplianceDocumentsContext } from "../ComplianceDocuments";
import useAuthorization, { isAuthorizedLE } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import QuickfiltersContainer from "../../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import {
  LegalEntityPasswordStatusQuickfilter,
  useLegalEntityPasswordStatusQuickFilter,
} from "../../../../containers/Quickfilters/LegalEntityPasswordStatusQuickfilter";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";

const Passwords = () => {
  const tRP = "pages.document_reports.compliance_documents.passwords";
  const { t } = useTranslation();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const hasAccessToPage = useAuthorization([
    AUTHORIZATIONS.LegalEntityAccess.ViewLegalEntitySI,
    AUTHORIZATIONS.LegalEntityAccess.ManageLegalEntitySI,
  ]);

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter, legalEntities },
  } = useContext(ComplianceDocumentsContext);

  const { legalEntityPasswordStatusQuickFilter, setLegalEntityPasswordStatusQuickFilter } =
    useLegalEntityPasswordStatusQuickFilter();

  const canChange = useMemo(() => {
    const leIdsToCheck = !!legalEntityOption?.legalEntityIds.length
      ? legalEntityOption?.legalEntityIds
      : legalEntities.map((el) => el.id);

    return (
      !!isAuthorizedLE(
        [AUTHORIZATIONS.LegalEntityAccess.ManageLegalEntitySI],
        currentUserAuthorizations,
        leIdsToCheck
      ) || !!currentUserAuthorizations?.canChangeAll
    );
  }, [currentUserAuthorizations, legalEntityOption, legalEntities]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: 5,
    },
  });

  const handleChangePage = (field: keyof Page, value: number) =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_LESI);

  const loadLegalEntitySIsCallback = useCallback(
    async (signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadLegalEntitySIs(
          {
            legalEntityIds: legalEntityOption?.legalEntityIds ?? [],
            ...legalEntityPasswordStatusQuickFilter.lePasswordStatus,
          },
          searchOptions,
          state.page,
          signal
        );

        if (!signal || !signal.aborted)
          setState((state) => ({
            ...state,
            items,
            totalItems,
            error: error as string,
          }));
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error: error as string }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [
      legalEntityOption?.legalEntityIds,
      searchOptions,
      state.page,
      legalEntityPasswordStatusQuickFilter.lePasswordStatus,
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();
    let timer: ReturnType<typeof setTimeout>;

    timer = setTimeout(() => loadLegalEntitySIsCallback(abortController.signal), searchOptions.query ? 500 : 0);

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [loadLegalEntitySIsCallback, searchOptions.query]);

  return (
    <>
      {hasAccessToPage && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Container>
            <ReportTableHeader
              title={`${t(`${tRP}.tab_label`)}`}
              searchOptions={searchOptions}
              handleChangeSearchOptions={handleChangeSearchOptions}
            >
              <QuickfiltersContainer>
                {legalEntityQuickFilter}

                <LegalEntityPasswordStatusQuickfilter
                  quickFilters={legalEntityPasswordStatusQuickFilter}
                  handleChangeQuickFilters={setLegalEntityPasswordStatusQuickFilter}
                />
              </QuickfiltersContainer>
            </ReportTableHeader>
            {canChange && (
              <AddEditPassword
                effect="add"
                handleNotifyParent={loadLegalEntitySIsCallback}
                legalEntity={selectedLegalEntity ?? null}
              />
            )}
            <Table
              items={state.items}
              totalItems={state.totalItems}
              page={state.page}
              handleChangePage={handleChangePage}
              handleNotifyParent={loadLegalEntitySIsCallback}
            />
          </Container>
        </LoadingOverlay>
      )}
    </>
  );
};

export default Passwords;
